import React, { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router"
import { useParams } from "react-router-dom"
import { FunctionComponent } from "react"
import TabPanel, { TabType } from "@murphy-frontend/web-core/components/Tabs/TabPanel"
import LevelRow from "./LevelRow"
import CategoryRow from "./CategoryRow"
import DescriptionCard from "./DescriptionCard"
import { useCustomer } from "../../../common/contexts/CustomerContext"
import { useGetCustomerBlocks, useGetRequirementLevels, useGetRequirementCategories } from "../api/queries"
import { useUpdateCustomerGoalLevel } from "../api/mutations"
import { RequirementCategories } from "@murphy-frontend/web-core/constants"
import { CustomerRequirementLevelDto, CustomerBlockDto, UpdateCustomerGoalLevelRequest } from "../api/models"
import { translateConstantsWithBackendTranslations } from "../../../common/services/translationService"
import { useUserPreferences } from "../../../common/contexts/UserPreferencesContext"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import { IconContext } from "react-icons/lib"
import { TbUsersGroup, TbBuildingBank, TbMessages, TbEdit } from "react-icons/tb"
import { RiCalendarEventFill } from "react-icons/ri"
import { LuPencilRuler } from "react-icons/lu"
import Button from "@murphy-frontend/web-core/components/Button"
import { IoChevronBackOutline } from "react-icons/io5"
import { RxRadiobutton } from "react-icons/rx"
import { CgRadioCheck } from "react-icons/cg"
import { ModalConfiguration } from "@murphy-frontend/web-core/components/Modal"
import { useModal } from "@murphy-frontend/web-core/contexts/ModalContext"
import CustomTooltip from "../../../common/CustomTooltip"


interface LevelViewProps {
	levelView: boolean
}
interface Grid {
	[key: number]: Array<CustomerBlockDto>
}

interface CardDescription {
	title: string
	description: string
}

interface BannerProps {
	image: any;
	title: string;
	body1: string;
	customStyles?: any;
}

const styles = {
	container: {
		width: '90rem',
		backgroundColor: 'white',
		height: '10rem',
		padding: 20,
		borderWidth: '1px',
		border: 'solid',
		marginBottom: 20,
		borderColor: '#00A098',
		overflow: 'hidden'
	},
	img: {
		width: 30,
		height: 30,
		marginRight: 20
	},
	title: {
		fontWeight: 'bold',
		fontSize: '16px'
	}
};

const Banner: FunctionComponent<BannerProps> = ({
	image,
	title,
	body1,
	customStyles = {},
}) => {
	return <div style={{ ...styles.container, ...customStyles }}>
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			<div style={{ display: 'flex', flexDirection: 'row', marginBottom: 10, alignItems: 'center' }}>
				<img src={image} style={styles.img} />
				<label style={styles.title}>{title}</label>
			</div>
			<div style={{ display: 'flex', flexDirection: 'row', marginTop: 10, justifyContent: 'space-between' }}>
				<label style={{ color: '#7F7F7F', width: '70%' }}>{body1}</label>
				<div style={{ flexDirection: 'row', display: 'flex', alignItems: 'center' }}>
					{/* <label style={{ textDecorationLine: 'underline', color: '#7F7F7F', cursor: 'pointer', marginRight: 20 }} onClick={() => { }}>Book a Demo</label> */}
							<Button buttonStyle='btn--primary' onClick={() => {
											window.open(
												"https://calendly.com/d/cs5c-gbr-zj7/go-premiun",
												"_blank",
												"noopener,noreferrer"
											);
}}>
								Go Premium
							</Button>
				</div>

			</div>
		</div>
	</div>;
};

const LevelView: FunctionComponent<LevelViewProps> = ({ levelView }) => {
	const [tabs, setTabs] = useState<TabType[]>([])
	const [selectedTabId, setSelectedTabId] = useState<number>(1)
	const [selectedCard, setSelectedCard] = useState<CardDescription>()
	const [currentGrid, setCurrentGrid] = useState<Grid>({})
	const [isLevelView, setIsLevelView] = useState(levelView)
	const [showGoal, setShowGoal] = useState(false)
	const [goalLevel, setGoalLevel] = useState<CustomerRequirementLevelDto>()
	const goalRef = useRef<HTMLDivElement>(null)
	const {openModal, closeModal} = useModal();
	const tooltipRef = useRef(null);

	let { customerId } = useParams()

	const navigate = useNavigate()
	const params = useParams()

	const { customer } = useCustomer()
	const { translations } = useUserPreferences()

	if (!customerId)
		customerId = customer.Id

	const handleClickInside = () => {
		setShowGoal(true)
	}

	function handleClickOutside(e: MouseEvent) {
		if (!goalRef.current?.contains(e.target as HTMLInputElement)) {
			setShowGoal(false)
		}
	}

	useEffect(() => {
		document.addEventListener("click", handleClickOutside)
		return () => document.removeEventListener("click", handleClickOutside)
	}, [])

	const handleCardOnLeave = (tabId?: number) => {
		if (!tabId) tabId = selectedTabId
		let tab
		if (isLevelView) {
			tab = requirementLevels?.find(lvl => tabId === lvl.Level)
			setSelectedCard({
				title: tab?.Name ?? "",
				description: tab?.Description ?? ""
			})
		} else {
			tab = requirementCategories.find(cat => tabId === cat.Id)
			setSelectedCard({
				title: `${tab.Name}`,
				description: tab.Description
			})
		}
	}

	const onClickTab = (tabId: number) => {
		setSelectedTabId(tabId)
		tooltipRef?.current?.closeTooltip();
	}

	useEffect(() => {
		if (!isLevelView && selectedTabId === 2 && params?.category && parseInt(params?.category) === 2) {
			setTimeout(() => {
				tooltipRef?.current?.openTooltip();
			}, 1000)
		}
	}, [selectedTabId])

	const toggleView = () => {
		if (!isLevelView) navigate(`/tmslevel/${customerId}`)
		else navigate(`/tmscategory/${customerId}`)
	}

	const {
		data: customerBlocks,
		isLoading: customerBlocksIsLoading,
		isPending: customerBlocksIsPending
	} = useGetCustomerBlocks(customerId)
	const {
		data: requirementLevels,
		isLoading: requirmentLevelesIsLoading,
		isPending: requirementLevelsIsPending
	} = useGetRequirementLevels(customerId)
	const { data: requirementCategories, isLoading: requirmentCategoriesIsLoading } = useGetRequirementCategories()
	const translatedRequirementCategories = translateConstantsWithBackendTranslations(
		RequirementCategories,
		translations
	)
	const { mutate: updateCustomerGoalLevel, isPending: updateCustomerGoalLevelIsLoading } = useUpdateCustomerGoalLevel()

	const icon = (categoryId: number) => {
		switch (categoryId) {
			case 1:
				return (
					<IconContext.Provider value={{ className: "categoryview-icon" }}>
						<TbUsersGroup />
					</IconContext.Provider>
				)
			case 2:
				return (
					<IconContext.Provider value={{ className: "categoryview-icon" }}>
						<TbBuildingBank />
					</IconContext.Provider>
				)
			case 3:
				return (
					<IconContext.Provider value={{ className: "categoryview-icon" }}>
						<RiCalendarEventFill />
					</IconContext.Provider>
				)
			case 4:
				return (
					<IconContext.Provider value={{ className: "categoryview-icon" }}>
						<LuPencilRuler />
					</IconContext.Provider>
				)
			case 5:
				return (
					<IconContext.Provider value={{ className: "categoryview-icon" }}>
						<TbMessages />
					</IconContext.Provider>
				)
			default:
				return null
		}
	}

	const setInitialTabs = (requirementLevels: Array<CustomerRequirementLevelDto>) => {
		let initialTabs: Array<TabType>
		if (isLevelView) {
			initialTabs = requirementLevels.map(lvl => {
				return {
					id: lvl.Level,
					displayText: lvl.Name,
					customClass: "tab-transparent-panel-tab",
					customSelectedClass: "tab-transparent-panel-tab-selected wrap-text",
					rightIcon: customer.IsFreemium && lvl.Level !==1 && require('../../../assets/img/premiumcrown.png')
				}
			})
		} else {
			initialTabs = requirementCategories.map(cat => {
				return {
					id: cat.Id,
					displayText: cat.Name,
					customClass: "tab-transparent-panel-tab-category",
					customSelectedClass: `tab-transparent-panel-tab-selected tab-transparent-panel-tab-selected--cat${cat.Id} wrap-text`,
					icon: icon(cat.Id)
				}
			})
		}
		setTabs(initialTabs)
	}

	const updateCustomerGrid = (tabId: number) => {
		const grid = customerBlocks.filter(req => req.Level === tabId)
		let category: Grid = {}
		grid.map((req: CustomerBlockDto) => {
			if (category[req.RequirementCategoryId])
				category[req.RequirementCategoryId] = [...category[req.RequirementCategoryId], { ...req }]
			else category[req.RequirementCategoryId] = [{ ...req }]
		})

		setCurrentGrid(category)
	}

	const updateLevelGrid = (categoryId: number) => {
		const grid = customerBlocks.filter(req => req.RequirementCategoryId === categoryId)

		let category: Grid = {}
		grid.map((req: CustomerBlockDto) => {
			if (category[req.Level]) {
				category[req.Level] = [...category[req.Level], { ...req }]
			} else category[req.Level] = [{ ...req }]
		})

		setCurrentGrid(category)
	}

	useEffect(() => {
		setIsLevelView(levelView)
		if (levelView) {
			tooltipRef?.current?.closeTooltip();
		}
		if (params?.category) setSelectedTabId(parseInt(params?.category))
		else setSelectedTabId(1)
	}, [levelView, params])

	useEffect(() => {
		if (customerBlocks) {
			if (isLevelView) updateCustomerGrid(selectedTabId)
			else updateLevelGrid(selectedTabId)
			if (requirementCategories) handleCardOnLeave(selectedTabId)
		}
	}, [
		selectedTabId,
		customerBlocks,
		isLevelView,
		requirementCategories,
		customerBlocksIsPending,
		requirementLevelsIsPending
	])

	useEffect(() => {
		if (!(requirmentLevelesIsLoading || requirmentCategoriesIsLoading)) {
			setInitialTabs(requirementLevels)
			setGoalLevel(requirementLevels.find(lvl => lvl.IsGoalLevel))
		}
	}, [requirementLevels, isLevelView, requirmentLevelesIsLoading, requirmentCategoriesIsLoading])

	if (customerBlocksIsLoading || requirmentLevelesIsLoading || requirmentCategoriesIsLoading) {
		return (
			<div className='spinner-container-global-center'>
				<Spinner />
			</div>
		)
	}

	const handleGoalSubmit = (gLevel: CustomerRequirementLevelDto) => {
		setGoalLevel(gLevel)
		const request: UpdateCustomerGoalLevelRequest = {
			CustomerId: customerId,
			Level: gLevel.Level
		}
		updateCustomerGoalLevel(request, {
			onSuccess: () => { }
		})
	}

	const showPremiumModal = () => {
		const modalConf: ModalConfiguration = {
			okCallback: () => { console.log("onclick") },
			onCloseModal: () => { console.log("on close") },
			type: 'tutorial',
			centerContent: false,
			body: <div>
				<div style={{ display: 'flex', flexDirection: 'column', backgroundColor: 'white', width: '30rem' }}>
					<div style={{ display: 'flex', height: '8rem', marginTop: 40, marginLeft: 30, marginRight: 30, marginBottom: 20, justifyContent: 'center', alignItems: 'center', backgroundColor: '#D9EBEA' }}>
						<img src={require('../../../assets/img/premium.png')} style={{ width: '100px' }} />
					</div>
					<div style={{ paddingLeft: 30, paddingRight: 30, display: 'flex', flexDirection: 'column' }}>
						<label style={{ marginBottom: 10, fontSize: 18, fontWeight: 'bold', color: '#222222' }}>Locked: This is a Premium feature</label>
						<label style={{ marginBottom: 10, fontSize: 14, color: '#7F7F7F' }}>
							To view Level 2, Level 3 and Level 4 content, you'll need to upgrade to Premium.
						</label>
						<label style={{ marginBottom: 10, fontSize: 14, color: '#7F7F7F' }}>These levels include advanced capabilities that help teams:</label>
						<ul style={{ marginLeft: 20, color: '#7F7F7F', fontSize: 14, listStyleType: 'disc' }}>
							<li>Strengthen internal and external coordination </li>
							<li>Train against real-world scenarios</li>
							<li>Reach higher operational maturity</li>
						</ul>
						<label style={{ marginTop: 10, fontSize: 14, color: '#7F7F7F' }}>Unlock full visibility and training by going Premium.</label>
						<div style={{ marginTop: 30, marginBottom: 30, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
							{/* <label style={{ textDecorationLine: 'underline', color: '#7F7F7F', marginRight: 20, cursor: 'pointer' }} onClick={() => closeModal()}>Book a Demo</label> */}
									<Button buttonStyle='btn--primary' onClick={() => {
											closeModal();
											window.open(
												"https://calendly.com/d/cs5c-gbr-zj7/go-premiun",
												"_blank",
												"noopener,noreferrer"
											);
}}>
								Go Premium
							</Button>
						</div>

					</div>
				</div>
			</div>,
			hideButton: true,
			position: 'center'
		}
		openModal(modalConf);
	}

	const goal = requirementLevels.find(lvl => lvl.IsGoalLevel)

	return (
		<>
			<section className='header-btns'>
				<div className='header-back-btn'>
					<Button buttonSize={"btn--medium"} isLightButton={true} onClick={() => navigate(`/tmsStartpage/${customerId}`)}>
						<IconContext.Provider value={{ className: "btn-icon" }}>
							<IoChevronBackOutline />
						</IconContext.Provider>
						&nbsp; Dashboard
					</Button>
				</div>
				<div className='header-right-btn level-goal-btn'>
					<div onClick={handleClickInside} ref={goalRef}>
						<Button buttonSize={"btn--medium"} isLightButton={true}>
							{goalLevel ? (
								<span>
									{translations.tmsGoal || "Goal"}: <span>{goalLevel.Name}</span> &nbsp;&nbsp;
								</span>
							) : (
								<span>{translations.tmsSetGoal || "Set goal "} &nbsp;&nbsp;</span>
							)}
							<IconContext.Provider value={{ className: "btn-icon" }}>
								<TbEdit />
							</IconContext.Provider>
						</Button>
					</div>
					{showGoal && (
						<ul className={"level-goal-window"}>
							{requirementLevels.map((el: CustomerRequirementLevelDto) => (
								<li
									className="level-goal-radio"
									key={el.Level}
									onClick={() => handleGoalSubmit(el)}
								>
									<IconContext.Provider value={{ className: "level-goal-icon" }}>
										{goalLevel && el.Level === goalLevel.Level ? <RxRadiobutton /> : <CgRadioCheck />}
									</IconContext.Provider>
									{el.Name}
								</li>
							))}
						</ul>
					)}
				</div>
			</section>
			{isLevelView && selectedTabId !== 1 && customer.IsFreemium && <Banner
				image={require('../../../assets/img/crown.png')}
				title={'Unlock more with Premium'}
				body1={'Get full access to Murphy’s expert-led training, advanced alerting tools, and custom dashboards — everything you need to level up your crisis preparedness.'}
			/>}
			<section className='level-tabs'>
				<TabPanel
					tabs={tabs}
					onClickTab={onClickTab}
					dataTooltip = {!isLevelView && selectedTabId === 2 && params?.category && parseInt(params?.category) === 2? 'organisation' : null}
					selectedTabId={selectedTabId}
					customClass='tab-solid-panel-tms'
				/>
				<div className='category-btn' onClick={toggleView}>  {translations.switchTo || "Switch to"}{" "}
					{isLevelView
						? (translations.categoryView || "category view")
						: (translations.levelView || "level view")
					}
				</div>
			</section>
			<DescriptionCard {...selectedCard} />
			{isLevelView ? (
				<section className='levelwrapper'>
					{Object.keys(currentGrid).map(key => (
						<LevelRow
							category={translatedRequirementCategories.find(el => el.id === parseInt(key)).value}
							cards={currentGrid[parseInt(key)]}
							categoryId={parseInt(key)}
							selectedTabId = {selectedTabId}
							key={key}
							onPremiumViewClick = {() => showPremiumModal()}
						/>
					))}
				</section>
			) : (
				<section className='categorywrapper'>
					{Object.keys(currentGrid)
						.reverse()
						.map(key => (
							<CategoryRow
								cards={currentGrid[parseInt(key)]}
								levelId={parseInt(key)}
								isGoalLevel={goal?.Level === (key ? parseInt(key) : 1)}
								key={key}
								onPremiumViewClick = {() => showPremiumModal()}
							/>
						))}
				</section>
			)}
			{customer.IsFreemium && <CustomTooltip
				id={'organisation'}
				ref={tooltipRef}
				title={'Explore by category'}
				body1={'You’re now viewing the Organisation category.'}
				place={'right'}
				buttons={[
					{ text: 'OK', type: 'button', onClick: () => {} }
				]}
			/>}
		</>
	)
}

export default LevelView
